import { type FC, useEffect, useState } from "react";
import {
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "@/shared/hooks";
import { DialogHeader } from "@/shared/components";
import { PollStatusType } from "@/types/graphql/site.generated";
import {
  CLOSED_POLL_VISIBLE_MS,
  selectPollToShow,
} from "../lib/selectPollToShow";
import { usePolls } from "../model/usePolls";
import type { PollHostType, SitePoll } from "../model/types";
import { PollCard } from "./PollCard";

export interface PollsOverlayProps {
  contentId: string;
  hostType: PollHostType;
  initialPolls: SitePoll[];
  currentTime: number;
  /** Контролы плеера видны: плашка опроса скрывается и появляется с ними. */
  controlsVisible: boolean;
  isCasting: boolean;
  /** Не используется: опрос открывается в диалоге, слот под плеером ему не нужен. */
  mobileSlot?: HTMLElement | null;
}

/**
 * Опрос поверх плеера (спека live-reactions-and-polls, «Витрина»). В кадре,
 * слева внизу на высоте панели реакций, только компактная плашка: вопрос,
 * «Пройти опрос» и крестик. Сам опрос (варианты, ответ, результаты)
 * открывается в диалоге и на десктопе, и на мобайле: раскрытая карточка в
 * кадре закрывала видео и налезала на сик-бар. В полноэкранном плеере
 * диалог рендерится внутрь него. Во время Chromecast скрыт.
 */
/** Сколько новый опрос висит сам, не дожидаясь контролов плеера. */
const ANNOUNCE_MS = 6000;

export const PollsOverlay: FC<PollsOverlayProps> = ({
  contentId,
  hostType,
  initialPolls,
  currentTime,
  controlsVisible,
  isCasting,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Корпус как у остальных диалогов витрины (PaymentDialog, SettingsDialog):
  // на телефоне во весь экран, шапка DialogHeader с крестиком.
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    polls,
    closedAtById,
    votedInSession,
    vote,
    voting,
    voteFailedPollId,
    clearVoteError,
  } = usePolls({
    contentId,
    initialPolls,
  });
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [, forceRender] = useState(0);
  // Какой опрос открыт в диалоге.
  const [openId, setOpenId] = useState<string | null>(null);
  // Новый опрос сначала показывается сам, даже при скрытом интерфейсе
  // плеера, а через ANNOUNCE_MS дальше живёт вместе с контролами.
  const [announcedId, setAnnouncedId] = useState<string | null>(null);

  const poll = selectPollToShow({
    polls,
    hostType,
    currentTime,
    nowMs: Date.now(),
    dismissedIds,
    closedAtById,
    votedInSessionIds: votedInSession,
  });

  // Закрытый опрос уходит через 15 с даже на паузе, когда currentTime не
  // меняется и перерисовки нет.
  const closedShownId =
    poll && poll.status === PollStatusType.ClosedType ? poll.id : null;
  useEffect(() => {
    if (!closedShownId) return;
    const timer = setTimeout(
      () => forceRender((value) => value + 1),
      CLOSED_POLL_VISIBLE_MS,
    );
    return () => clearTimeout(timer);
  }, [closedShownId]);

  const pollIdForAnnounce = poll?.id ?? null;
  useEffect(() => {
    if (!pollIdForAnnounce) return;
    setAnnouncedId(pollIdForAnnounce);
    const timer = setTimeout(() => setAnnouncedId(null), ANNOUNCE_MS);
    return () => clearTimeout(timer);
  }, [pollIdForAnnounce]);

  // Ошибка голоса относится к показанному опросу: сменился опрос, сброс.
  const shownPollId = poll?.id ?? null;
  useEffect(() => {
    clearVoteError();
  }, [shownPollId, clearVoteError]);

  if (isCasting || !poll) return null;

  const dismiss = () => setDismissedIds((prev) => new Set(prev).add(poll.id));
  const open = () => setOpenId(poll.id);
  const canVote =
    !poll.viewerVote?.length && poll.status !== PollStatusType.ClosedType;

  return (
    <>
      <Fade in={controlsVisible || announcedId === poll.id}>
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            left: 12,
            bottom: { xs: 88, md: 96 },
            zIndex: 210,
            maxWidth: { xs: "min(60%, 280px)", md: 340 },
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            pl: 1.25,
            pr: 0.5,
            py: 0.5,
            borderRadius: 3,
          }}
        >
          <ButtonBase
            onClick={open}
            sx={{ flex: 1, minWidth: 0, justifyContent: "flex-start" }}
          >
            <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
              {poll.question}
            </Typography>
          </ButtonBase>
          {canVote && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={open}
              sx={{ flexShrink: 0, py: 0.25, px: 1, minWidth: 0 }}
            >
              {t("polls.takePoll")}
            </Button>
          )}
          <IconButton
            size="small"
            aria-label={t("polls.dismiss")}
            onClick={dismiss}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Fade>
      <Dialog
        open={openId === poll.id}
        onClose={() => setOpenId(null)}
        // В полноэкранном плеере виден только его контейнер.
        container={() => document.fullscreenElement ?? document.body}
        fullWidth
        maxWidth="xs"
        scroll="body"
        fullScreen={fullScreen}
      >
        <DialogHeader title={poll.question} onClose={() => setOpenId(null)} />
        <DialogContent sx={{ pt: 0.5, pb: 3 }}>
          <PollCard
            key={poll.id}
            poll={poll}
            hostType={hostType}
            placement="slot"
            withHeader={false}
            defaultExpanded
            voting={voting}
            voteFailed={voteFailedPollId === poll.id}
            onVote={(optionIds) => void vote(poll.id, optionIds)}
            onDismiss={() => setOpenId(null)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
